import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/category/all', label: t('nav.category') },
    { path: '/about', label: t('nav.about') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-screen-2xl mx-auto">
          <Link
            to="/"
            className="flex items-center gap-2 font-heading text-2xl text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-book-read-line text-2xl"></i>
            </span>
            <span className="hidden sm:inline">{t('site.name')}</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium transition-colors whitespace-nowrap py-1 ${
                  isActive(link.path)
                    ? 'text-primary-600'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary-500 rounded-full"></span>
                )}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-foreground-600 text-lg"></i>
            </button>
            <Link
              to="/about"
              className="px-4 py-2 text-sm font-medium text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.register')}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl text-foreground-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {searchOpen && (
          <div className="hidden md:block pb-3">
            <div className="relative max-w-md mx-auto">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder={t('search.placeholder')}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
              />
            </div>
          </div>
        )}
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <div className="flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium py-2 px-3 rounded-lg transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="relative mt-2">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder={t('search.placeholder')}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 transition-all"
              />
            </div>
            <Link
              to="/about"
              className="mt-2 px-4 py-2.5 text-sm font-medium text-center text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav.register')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}